import { ArrowLeft, Gift, Zap, Bug, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChangelogEntry {
    version: string;
    date: string;
    type: 'major' | 'minor' | 'patch';
    changes: {
        type: 'feature' | 'improvement' | 'fix';
        title: string;
        description?: string;
    }[];
}

const changelog: ChangelogEntry[] = [
    {
        version: '1.0.3',
        date: 'January 14, 2026',
        type: 'minor',
        changes: [
            { type: 'feature', title: 'Slack Integration', description: 'Connect your Slack workspace to receive messages in your inbox' },
            { type: 'feature', title: 'Notion Integration', description: 'Sync pages and updates from your Notion workspace' },
            { type: 'feature', title: 'Data Export', description: 'Export all your data as JSON or CSV files' },
            { type: 'feature', title: 'Voice Data Management', description: 'Control how your voice data is stored and retained' },
            { type: 'feature', title: 'AI Training Mode', description: 'Teach the AI your scheduling preferences' },
            { type: 'improvement', title: 'Focus Mode Enhancements', description: 'Added DND toggle and breathing exercise breaks' },
            { type: 'improvement', title: 'Energy Indicator', description: 'New circular energy meter with productivity recommendations' },
            { type: 'fix', title: 'Calendar White Screen', description: 'Fixed crash when loading calendar page' },
            { type: 'fix', title: 'Event Count Bug', description: 'Home page now shows only today\'s meetings' },
        ],
    },
    {
        version: '1.0.2',
        date: 'January 10, 2026',
        type: 'minor',
        changes: [
            { type: 'feature', title: 'AI Schedule Negotiation', description: 'Smart banners for schedule conflicts with one-tap solutions' },
            { type: 'feature', title: 'Focus Mode Task Context', description: 'View project info, subtasks, and AI tips while focusing' },
            { type: 'feature', title: 'Weather Widget', description: 'See local weather in your morning briefing' },
            { type: 'improvement', title: 'Inbox Bulk Actions', description: 'Select multiple items for batch operations' },
            { type: 'improvement', title: 'Calendar Mini Widget', description: 'Quick event preview on the home page' },
        ],
    },
    {
        version: '1.0.1',
        date: 'January 5, 2026',
        type: 'patch',
        changes: [
            { type: 'feature', title: 'Inbox Zero Celebration', description: 'Confetti animation when you clear your inbox' },
            { type: 'feature', title: 'AI Suggestions Card', description: 'Smart suggestions for tasks and schedule optimization' },
            { type: 'fix', title: 'Timezone Handling', description: 'Fixed time display issues across different timezones' },
        ],
    },
    {
        version: '1.0.0',
        date: 'January 1, 2026',
        type: 'major',
        changes: [
            { type: 'feature', title: 'Initial Release', description: 'Way2Will OS is here! Your AI-powered productivity command center.' },
            { type: 'feature', title: 'Task Management', description: 'Create, organize, and prioritize your tasks' },
            { type: 'feature', title: 'Calendar Integration', description: 'Sync with Google Calendar for unified scheduling' },
            { type: 'feature', title: 'AI Voice Assistant', description: 'Natural language task creation and management' },
            { type: 'feature', title: 'Morning Briefings', description: 'Start your day with an AI-generated overview' },
        ],
    },
];

function getChangeIcon(type: 'feature' | 'improvement' | 'fix') {
    switch (type) {
        case 'feature':
            return <Sparkles className="w-4 h-4 text-[#E8A547]" />;
        case 'improvement':
            return <Zap className="w-4 h-4 text-[#0D5F6B]" />;
        case 'fix':
            return <Bug className="w-4 h-4 text-[#3DA58A]" />;
    }
}

function getChangeLabel(type: 'feature' | 'improvement' | 'fix') {
    switch (type) {
        case 'feature':
            return 'New';
        case 'improvement':
            return 'Improved';
        case 'fix':
            return 'Fixed';
    }
}

export default function Changelog() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#F5F8FA]">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F8FA] transition"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#0D5F6B]" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Gift className="w-5 h-5 text-[#E8A547]" />
                        <h1 className="text-xl font-semibold text-[#1A2832]">What's New</h1>
                    </div>
                </div>
            </div>

            {/* Changelog Content */}
            <div className="max-w-2xl mx-auto px-4 py-6">
                {changelog.map((entry, index) => (
                    <div key={entry.version} className="mb-8">
                        {/* Version Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${entry.type === 'major'
                                    ? 'bg-[#E8A547] text-white'
                                    : entry.type === 'minor'
                                        ? 'bg-[#0D5F6B] text-white'
                                        : 'bg-[#DCE4EA] text-[#1A2832]'
                                }`}>
                                v{entry.version}
                            </span>
                            <span className="text-sm text-[#7B8C9B]">{entry.date}</span>
                            {index === 0 && (
                                <span className="px-2 py-0.5 rounded bg-[#D5F2EA] text-[#3DA58A] text-xs font-semibold">
                                    Latest
                                </span>
                            )}
                        </div>

                        {/* Changes List */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                            {entry.changes.map((change, changeIndex) => (
                                <div
                                    key={changeIndex}
                                    className={`p-4 ${changeIndex < entry.changes.length - 1 ? 'border-b border-[#E5EBF0]' : ''}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[#F5F8FA] flex items-center justify-center flex-shrink-0">
                                            {getChangeIcon(change.type)}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${change.type === 'feature'
                                                        ? 'bg-[#FFF4E5] text-[#E8A547]'
                                                        : change.type === 'improvement'
                                                            ? 'bg-[#D9EEF2] text-[#0D5F6B]'
                                                            : 'bg-[#D5F2EA] text-[#3DA58A]'
                                                    }`}>
                                                    {getChangeLabel(change.type)}
                                                </span>
                                                <span className="font-semibold text-[#1A2832]">{change.title}</span>
                                            </div>
                                            {change.description && (
                                                <p className="text-sm text-[#7B8C9B]">{change.description}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
